/**
 * The sync decision procedure — pure, and therefore unit-tested.
 *
 * Given what the browser knows and what the server returned, decide what to do.
 * Every rule here exists because its absence loses a real customer's cart; the
 * comments name the failure so a future simplification cannot quietly undo one.
 *
 * See docs/CART_SYNC_PLAN.md §6.6.
 */

import type { CanonicalCart } from "./canonical.js";
import { hashCanonical } from "./canonical.js";
import type {
  ClearEvidence,
  PublicCartState,
  SyncGuards,
  SyncState,
} from "./types.js";
import { ClearedReason } from "./types.js";

/** How long a deliberate clear stays believable. */
export const CLEAR_EVIDENCE_TTL_MS = 10 * 60 * 1000;

/**
 * How long an unexplained empty local cart must persist before it counts as
 * state loss.
 *
 * A purchase and an expired cart cookie are indistinguishable at the instant
 * they are observed: both leave an empty browser cart under a server cart that
 * still has lines. The tiebreaker is the purchase tombstone, which arrives
 * 5-7 s after checkout — so acting on the first observation restores the cart
 * the buyer has just paid for. `initCartSync` made that near-certain rather than
 * rare by firing a cycle the moment the buyer returns from checkout.
 *
 * 20 s clears the observed lag with headroom for a slow delivery. The trade is
 * deliberate and one-sided: a genuine cookie loss restores 20 s later than it
 * used to, which is slow; a restored purchased cart reads as "my order didn't
 * go through" and gets ordered again, which is wrong.
 */
export const EMPTY_LOCAL_SETTLE_MS = 20 * 1000;

export const SyncAction = {
  /** Missing information. Do nothing at all — never mutate a cart on a guess. */
  Abort: "abort",
  /**
   * An empty local cart we cannot yet explain. Start (or keep) the settle timer
   * and look again — do NOT treat it as state loss until it has held.
   */
  WaitForSettle: "waitForSettle",
  /** A previous apply did not finish; finish it before anything else. */
  ResumeApply: "resumeApply",
  /** Local bookkeeping belongs to someone else; wipe it and the Shopify cart. */
  ClearLocalCart: "clearLocalCart",
  /** Local bookkeeping is stale/foreign; wipe bookkeeping only, keep the cart. */
  ResetState: "resetState",
  /** Server has nothing (or nothing usable); publish what this device holds. */
  PushFirstContact: "pushFirstContact",
  /** Local changed since the last sync; publish the difference. */
  PushDrift: "pushDrift",
  /** Server moved ahead; adopt it. */
  ApplyServer: "applyServer",
  /** Nothing to do. */
  Idle: "idle",
} as const;
export type SyncAction = (typeof SyncAction)[keyof typeof SyncAction];

export interface Decision {
  readonly action: SyncAction;
  /**
   * Stash the local lines into server history before applying, so a cart this
   * device is about to lose is one click from recovery.
   */
  readonly stashLocalFirst?: boolean;
  /** Human-readable reason, for breadcrumbs and tests. */
  readonly reason: string;
}

export interface DecisionInput {
  readonly identity: { readonly customerKey: string; readonly locationId: number };
  readonly state: SyncState | null;
  readonly guards: SyncGuards;
  /** null when /cart.js did not return a usable body. */
  readonly local: CanonicalCart | null;
  /** null when GET /cart-sync did not return a usable body. */
  readonly server: PublicCartState | null;
  readonly now: number;
}

/**
 * Is this evidence of a deliberate clear still trustworthy?
 *
 * Both conditions matter. Without the TTL a stuck flag disables the empty-local
 * guard forever, so a cart-cookie expiry days later reads as a clear and empties
 * the shared cart. Without the version binding, evidence from a clear that was
 * rejected (409) still suppresses the guard.
 */
export function isClearEvidenceValid(
  evidence: ClearEvidence | null,
  server: PublicCartState,
  now: number,
): boolean {
  if (!evidence) return false;
  if (now - evidence.at > CLEAR_EVIDENCE_TTL_MS) return false;
  if (server.version === evidence.baseVersion) return true;
  // Our own clear landed.
  return server.version === evidence.baseVersion + 1 && server.cleared;
}

/** True when the server state is a real tombstone (empty AND explicitly cleared). */
export function isTombstone(server: PublicCartState): boolean {
  return server.cleared && server.items.length === 0;
}

/**
 * True when applying the server state would delete lines this device holds.
 * Used to decide whether to stash first.
 */
function wouldLoseLocalWork(
  local: CanonicalCart,
  server: PublicCartState,
): boolean {
  if (local.size === 0) return false;
  const serverKeys = new Set(
    server.items.map(
      (item) => `${item.variantId}|${item.properties._warehouse_code}`,
    ),
  );
  let loses = false;
  local.forEach((_quantity, key) => {
    if (!serverKeys.has(key)) loses = true;
  });
  return loses;
}

export function decide(input: DecisionInput): Decision {
  const { identity, state, guards, local, server, now } = input;

  // ---- Step 0: information and sanity -------------------------------------

  // Absence of information is never evidence of an empty cart.
  if (!server || !local) {
    return { action: SyncAction.Abort, reason: "server or local cart unavailable" };
  }

  // A half-finished apply must complete before anything reads the cart as truth.
  if (guards.applyInProgress) {
    return { action: SyncAction.ResumeApply, reason: "apply in progress" };
  }

  const serverIsTombstone = isTombstone(server);
  const serverEmptyNotCleared = server.items.length === 0 && !server.cleared;

  if (state && state.customerKey !== identity.customerKey) {
    // A different person is now logged in on this browser. Their cart must not
    // inherit the previous account's lines.
    return {
      action: SyncAction.ClearLocalCart,
      reason: "customer changed on this device",
    };
  }

  if (state && state.locationId !== identity.locationId) {
    // Same person, different company location. The cart belongs to the other
    // location's key — keep it locally, just stop treating our bookkeeping as
    // describing it. Clearing here would delete real work.
    return {
      action: SyncAction.ResetState,
      reason: "company location changed",
    };
  }

  if (state && server.version < state.lastAppliedVersion) {
    // Routine, not exotic: the server purges tombstoned rows after 90 days, and
    // a privacy deletion does the same. Reset bookkeeping only — never answer a
    // regression by deleting the customer's cart.
    return {
      action: SyncAction.ResetState,
      reason: "server version regressed (row purged or reset)",
    };
  }

  // New device / wiped state / first run.
  if (!state) {
    if (server.version === 0 || serverEmptyNotCleared) {
      // Nothing authoritative on the server. Publish what we have; never empty
      // a real cart because the server has no opinion yet. This is cutover day
      // for every existing customer.
      return local.size > 0
        ? { action: SyncAction.PushFirstContact, reason: "first contact" }
        : { action: SyncAction.Idle, reason: "first contact, nothing to publish" };
    }
    return {
      action: SyncAction.ApplyServer,
      stashLocalFirst: wouldLoseLocalWork(local, server),
      reason: "new device adopting server cart",
    };
  }

  // ---- Step 1: the empty-local guard --------------------------------------

  const drifted = hashCanonical(local) !== state.lastAppliedHash;

  if (local.size === 0 && server.items.length > 0) {
    if (!isClearEvidenceValid(guards.clearEvidence, server, now)) {
      // Shopify's cart cookie lives ~14 days; ITP and a cleared browser do the
      // same thing sooner. An empty cart with no proof of a deliberate clear is
      // state LOSS, and pushing it would destroy the saved cart this feature
      // exists to preserve.
      //
      // But a completed PURCHASE looks identical right now, and its tombstone is
      // still seconds away. So hold the verdict until the emptiness has survived
      // EMPTY_LOCAL_SETTLE_MS — by then a purchase has announced itself and this
      // branch is never reached, because the tombstone rule below takes over.
      const emptySince = guards.emptyLocalSince;
      if (emptySince === null || now - emptySince < EMPTY_LOCAL_SETTLE_MS) {
        return {
          action: SyncAction.WaitForSettle,
          reason: "local cart empty — waiting to see whether a purchase explains it",
        };
      }
      return {
        action: SyncAction.ApplyServer,
        reason: "local cart empty without clear evidence — treating as state loss",
      };
    }
  }

  // ---- Step 2: tombstones and drift ---------------------------------------

  if (serverIsTombstone && server.version > state.lastAppliedVersion) {
    if (server.clearedReason === ClearedReason.Expired && local.size > 0) {
      // Server housekeeping on a cart the customer may still be using weekly —
      // a pull is not activity, so the 90-day clock is not refreshed by use.
      // Republish rather than delete.
      return {
        action: SyncAction.PushDrift,
        reason: "expired tombstone over a live local cart — reviving",
      };
    }
    return {
      action: SyncAction.ApplyServer,
      stashLocalFirst: local.size > 0,
      reason: `tombstone (${server.clearedReason ?? "unknown"})`,
    };
  }

  if (server.version > state.lastAppliedVersion) {
    if (serverEmptyNotCleared && local.size > 0) {
      // Reachable via remove-ops. Not a tombstone, so it must never empty a
      // real cart — publish instead.
      return {
        action: SyncAction.PushDrift,
        reason: "server empty but not cleared — republishing local lines",
      };
    }
    return {
      action: SyncAction.ApplyServer,
      stashLocalFirst: wouldLoseLocalWork(local, server),
      reason: "server ahead",
    };
  }

  if (drifted) {
    return { action: SyncAction.PushDrift, reason: "local drift" };
  }

  return { action: SyncAction.Idle, reason: "in sync" };
}
